/**
 * Mock data for the /calendar surface preview.
 *
 * Hand-curated events anchored around today (2026-05-26) so reviewers see:
 *   - 1 completed past holiday (Mother's Day)
 *   - 1 past missed event (Sarah's birthday)
 *   - 1 imminent event (Mom's birthday in 5 days)
 *   - 1 mid-month event in June (with AI-loading state)
 *   - 1 future holiday (Father's Day)
 *   - 1 future event in July (so month-nav reveals more content)
 *
 * Shape mirrors what a `theaWebMaterializedCalendar` doc would carry on the
 * BE — recipient name/emoji denormalized, product image strip from the rec
 * doc. Holidays are pure, no recipient connection (per Slice-2 punt: holidays
 * live on /occasion/{slug} pages rather than per-recipient associations).
 */

import { HOLIDAYS, isHolidayEventType, type HolidayEventType } from './holidays';
import { topicHubPath } from '../hub/topicHub';

export interface MockCalendarRecipient {
  id: string;
  name: string;
  emoji: string;
}

/**
 * Person events have a known recipient (birthday, anniversary). Holiday
 * events have no recipient (per Slice-1 design — holidays don't connect
 * to specific people).
 */
export type PersonEventType = 'birthday' | 'anniversary';
export type CalendarEventType = PersonEventType | HolidayEventType;

export interface MockCalendarEvent {
  id: string;
  /** YYYY-MM-DD, local-day semantic. */
  date: string;
  eventType: CalendarEventType;
  /** Primary recipient for person events. Null for holiday events. */
  recipient: MockCalendarRecipient | null;
  isComplete: boolean;
  /** Solid color tokens used as themed placeholder tiles on HOLIDAY cards
   *  (Mother's Day, Father's Day, etc.) where no recipient-specific
   *  preview images are available. Person events leave this empty. */
  productThumbs: string[];
  /** Real product image URLs from the recipient's materializedPeople
   *  preview (up to 4). Set for person events when the recipient has
   *  saved items; empty otherwise. When set, renders as a PreviewCollage
   *  matching the People-tab board-tile vocabulary. */
  previewImages?: string[];
  /** When true, the card shows the AI-loading shimmer (matches iOS pattern). */
  isAiLoading?: boolean;
  /** For guide-linked holidays (Halloween, Thanksgiving): the gift-guide
   *  listing key this card taps to (`/guide/{guideSlug}`). Copied from the
   *  HolidayEntry; person events and occasion holidays leave it undefined. */
  guideSlug?: string;
}

const PINK = '#F4C4C9';
const SAGE = '#C7DBC1';
const CLAY = '#D9B19E';
const CREAM_GOLD = '#E8D9A8';
const TAN = '#D8B68F';
const DUSTY_BLUE = '#B6C8D6';
const ROSE = '#E5BFC5';

export const MOCK_CALENDAR_EVENTS: MockCalendarEvent[] = [
  {
    id: 'evt-mothers-day-2026',
    date: '2026-05-10',
    eventType: 'mothers_day',
    recipient: null,
    isComplete: true,
    productThumbs: [PINK, ROSE, CREAM_GOLD, CLAY],
  },
  {
    id: 'evt-sarah-bday',
    date: '2026-05-24',
    eventType: 'birthday',
    recipient: { id: 'sarah', name: 'Sarah', emoji: '🤝' },
    isComplete: false,
    productThumbs: [DUSTY_BLUE, CREAM_GOLD, TAN],
  },
  {
    id: 'evt-mom-bday',
    date: '2026-05-31',
    eventType: 'birthday',
    recipient: { id: 'mom', name: 'Mom', emoji: '🌷' },
    isComplete: false,
    productThumbs: [PINK, ROSE, CLAY, CREAM_GOLD],
  },
  {
    id: 'evt-dad-bday',
    date: '2026-06-14',
    eventType: 'birthday',
    recipient: { id: 'dad', name: 'Dad', emoji: '⛳' },
    isComplete: false,
    isAiLoading: true,
    productThumbs: [SAGE, TAN, DUSTY_BLUE],
  },
  {
    id: 'evt-fathers-day-2026',
    date: '2026-06-21',
    eventType: 'fathers_day',
    recipient: null,
    isComplete: false,
    productThumbs: [SAGE, TAN, CLAY, DUSTY_BLUE],
  },
  {
    id: 'evt-ellie-bday',
    date: '2026-07-04',
    eventType: 'birthday',
    recipient: { id: 'ellie', name: 'Ellie', emoji: '🎀' },
    isComplete: false,
    productThumbs: [ROSE, PINK, CREAM_GOLD],
  },
];

/**
 * eventType → display label, built once from `HOLIDAYS`.
 *
 * `holidays.ts` is the single source of truth for holiday names: every
 * entry already carries a board-approved `label` (including the exact
 * `"Eid al-Fitr (approx.)"` spelling), so restating them here is pure
 * duplication — and the kind that silently rots. This used to be a
 * hand-maintained switch whose `default` returned the raw event type;
 * when THE-748 took the list from 9 holidays to 26, the 17 new ones fell
 * through and cards rendered `rosh_hashanah` (THE-753). Reading the entry
 * means a 27th holiday is labelled the moment it is added.
 *
 * Keyed by `string` rather than `HolidayEventType` so the lookup accepts a
 * `CalendarEventType` (which includes the person types) without a cast.
 */
const HOLIDAY_LABELS: ReadonlyMap<string, string> = new Map(
  HOLIDAYS.map((holiday) => [holiday.eventType, holiday.label]),
);

/**
 * `birthday` / `anniversary` are not holidays — they belong to a recipient
 * and have no `HolidayEntry`, so their labels do live here. Typed as a
 * total `Record` over `PersonEventType`, which means adding a person event
 * type is a typecheck error until it is labelled (the one guarantee the
 * old switch did give us, kept).
 */
const PERSON_EVENT_LABELS: Record<PersonEventType, string> = {
  birthday: 'Birthday',
  anniversary: 'Anniversary',
};

export function formatEventLabel(eventType: MockCalendarEvent['eventType']): string {
  const holidayLabel = HOLIDAY_LABELS.get(eventType);
  if (holidayLabel !== undefined) return holidayLabel;

  const personLabel = PERSON_EVENT_LABELS[eventType as PersonEventType];
  if (personLabel !== undefined) return personLabel;

  // Unreachable for a well-typed `CalendarEventType`. Kept for an event
  // type that reaches us from BE data ahead of the FE knowing about it:
  // the raw key is ugly but it is better than an empty card title.
  return eventType;
}

/**
 * Card title text. Person events display "Sarah's Birthday"; holiday events
 * display the holiday name directly.
 */
export function eventTitle(event: MockCalendarEvent): string {
  if (event.recipient) {
    return `${event.recipient.name}'s ${formatEventLabel(event.eventType)}`;
  }
  return formatEventLabel(event.eventType);
}

/**
 * Where tapping the card body should navigate.
 *
 * - Occasion holidays (mothers_day, fathers_day, teacher_appreciation) →
 *   the corresponding `/occasion/{slug}` page (Thea's canonical "holiday
 *   board"), via HOLIDAY_OCCASION_SLUG below.
 * - Guide holidays (halloween, thanksgiving) → the curated gift guide
 *   `/guide/{event.guideSlug}`, carried on the event from its HolidayEntry.
 * - Person events (birthday, anniversary on a single recipient) →
 *   the recipient's board `/board/{recipientId}`.
 * - Anything we don't know how to route → null (caller can no-op).
 *
 * Occasion beats guide when (hypothetically) both are present; in practice
 * holidays.ts enforces the two slugs are mutually exclusive.
 */
const HOLIDAY_OCCASION_SLUG: Partial<Record<MockCalendarEvent['eventType'], string>> = {
  mothers_day: 'mothers_day',
  fathers_day: 'fathers_day',
  teacher_appreciation: 'teacher_appreciation',
  // valentines_day, easter, hanukkah, christmas intentionally omitted — no
  // /occasion/ route exists for them, and they have no guide either, so they
  // stay informational. halloween + thanksgiving route via `guideSlug`
  // instead (see holidays.ts). Adding an /occasion/ destination means adding
  // the route in OccasionRoute.tsx + a key here (together).
};

/**
 * Event-ID prefix used by the curated MOCK_CALENDAR_EVENTS fixtures
 * (e.g. `evt-sarah-bday`, `evt-mom-bday`). When the calendar is showing
 * mock fallback data, the recipient IDs are *not* real Firestore doc
 * IDs — routing to `/board/{mockId}` lands on a BoardRoute that can't
 * resolve the recipient and bounces to `/quiz`, which looks broken.
 *
 * Suppress navigation for mock person events so the demo preview reads
 * as "preview state" rather than "broken click → quiz". Holiday events
 * still navigate normally because they go to occasion routes that exist
 * irrespective of recipient data.
 *
 * Real materializedComingUp events use IDs like
 * `${recipientId}_birthday_2026` (no `evt-` prefix) — they route fine.
 */
const MOCK_EVENT_ID_PREFIX = 'evt-';

/**
 * The Gift Guide Hub collection key a holiday WOULD have: its event type in
 * kebab case (`fourth_of_july` -> `fourth-of-july`), which is the slug shape
 * the hub's occasion keys use (`theaWebHubOccasions/current`). Whether that
 * collection actually EXISTS is not decided here — the caller passes the live
 * set of keys from that doc, so the board can add or remove a collection with
 * no deploy and a missing one simply falls back to today's destination.
 */
export function holidayCollectionKey(eventType: HolidayEventType): string {
  return eventType.replace(/_/g, '-');
}

/**
 * `hubCollections` — the hub's occasion collections, keyed by their kebab-case
 * collection key, each holding the published guide slugs it collects — passed
 * ONLY when the Gift Guide Hub gate is enabled AND the occasions doc has
 * loaded. Absent/empty (every real user today, the gate's pending state, a
 * loading or failed read) means exactly today's routing. Person events never
 * consult it — the hub has `birthday` and `anniversary` collections, and a
 * person's birthday must still open THEIR board.
 *
 * When present and a HOLIDAY's collection exists:
 *   - more than one guide → `/gift-guides/{key}`, the collection itself.
 *   - exactly one guide → `/guide/{thatGuide}` directly (THE-1403). A
 *     one-guide collection is a shortcut to a single page the holiday
 *     already has its own destination for; opening the collection page
 *     first would be a step the shopper only has to retrace.
 */
export function eventDestination(
  event: MockCalendarEvent,
  hubCollections?: ReadonlyMap<string, readonly string[]>,
): string | null {
  if (hubCollections?.size && isHolidayEventType(event.eventType)) {
    const key = holidayCollectionKey(event.eventType);
    const guideSlugs = hubCollections.get(key);
    if (guideSlugs) {
      return guideSlugs.length === 1 ? `/guide/${guideSlugs[0]}` : topicHubPath(key);
    }
  }
  const slug = HOLIDAY_OCCASION_SLUG[event.eventType];
  if (slug) return `/occasion/${slug}`;
  if (event.guideSlug) return `/guide/${event.guideSlug}`;
  if (event.id.startsWith(MOCK_EVENT_ID_PREFIX)) return null;
  if (event.recipient) return `/board/${event.recipient.id}`;
  return null;
}

/**
 * Coarse shape of an `eventDestination()` result, safe to report to
 * analytics (THE-1403): says WHERE a tap landed without naming WHAT it was —
 * no slug, no collection key, nothing that reconstructs a specific holiday
 * or recipient the way the path string itself would.
 */
export type EventDestinationKind = 'board' | 'guide' | 'collection' | 'occasion' | 'calendar';

export function destinationKind(dest: string | null): EventDestinationKind {
  if (dest?.startsWith('/board/')) return 'board';
  if (dest?.startsWith('/gift-guides/')) return 'collection';
  if (dest?.startsWith('/guide/')) return 'guide';
  if (dest?.startsWith('/occasion/')) return 'occasion';
  return 'calendar';
}
